import React from "react";
import PropTypes from "prop-types";
import Typography from "../../../components/atoms/typography";
import CustomImage from "../../../components/atoms/customImage";
import { campaignEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";

/**
 * WebNativeDisplayBanners Component
 *
 * Renders a grid of clickable banner images based on the provided `bannerData` prop.
 * It parses a JSON string and maps over the data to show square images with links.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.bannerData - Banner configuration object
 * @param {Object} props.campaignEventData - Banner campaign object
 * @param {string} props.bannerData.title - The title shown above the image grid
 * @param {string} props.bannerData.data - JSON string containing an array of image and redirection URL objects
 * @returns {JSX.Element} The rendered banner display
 */
const WebNativeDisplayBanners = ({ bannerData, campaignEventData }) => {
  const parsedBannerData = bannerData?.data || [];

  const { trackEvent } = useAnalytics();
  /**
   * Handle click event on banner items.
   * It tracks the event if the item has a campaign_id.
   * @param {Object} item - The banner item clicked
   * @returns {void}
   */
  const onBannerClick = (item) => {
    if (item?.campaign_id) {
      trackEvent(campaignEvents.RENDER_NOTIFICATION_CLICKED, campaignEventData[item?.campaign_id], ["clevertap"]);
    }
  };

  return (
    <>
      <Typography variant="p" Tag="p" classes="text-16 md:text-14 lg:text-16 font-600 pb-12 leading-18 xxl:text-18">
        {bannerData?.title}
      </Typography>

      {parsedBannerData?.length > 0 && (
        <div className="grid grid-cols-3 gap-12">
          {parsedBannerData.map((item, index) => (
            <a
              key={index}
              href={item?.redirectionUrl}
              onClick={() => onBannerClick(item)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="aspect-square h-full w-full overflow-hidden rounded">
                <CustomImage
                  src={item?.imageUrl}
                  alt={`banner-${index}`}
                  width={150}
                  height={150}
                  className="h-full w-full rounded"
                  isPriority={false}
                />
              </div>
            </a>
          ))}
        </div>
      )}
    </>
  );
};

WebNativeDisplayBanners.propTypes = {
  campaignEventData: PropTypes.object,
  bannerData: PropTypes.shape({
    title: PropTypes.string,
    data: PropTypes.array,
  }),
};

export default React.memo(WebNativeDisplayBanners);
